import { Button, H5, H6, InputCheckbox } from "@sellar-io/components";
import {
  OrderGetResponseType,
  OpenOrderListResponseType,
} from "@sellar-io/api-v2/types";
import { formatCurrency, pluralize } from "@sellar-io/common";

import dayjs from "dayjs";
import { useState } from "react";
import { useOrder } from "../../../../../hooks/api/v2/order/useOrder";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";

const CreateNewOrder = ({
  checked,
  onCheckToggled,
}: {
  checked: boolean;
  onCheckToggled: (newState: boolean) => void;
}) => {
  return (
    <div
      className="flex cursor-pointer items-center justify-between rounded-lg border border-zinc-300 p-4"
      onClick={() => onCheckToggled(!checked)}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="shrink-0">
          <InputCheckbox
            variant="circular"
            required={false}
            checked={checked}
          />
        </div>
        <H5 className="text-zinc-800">Continue to create a new order</H5>
      </div>
    </div>
  );
};

const RecentOrderList = ({
  openOrderList,
  onOpenOrderSelected,
  selectedOrderId,
}: {
  openOrderList: OpenOrderListResponseType;
  onOpenOrderSelected: (selectedOrderId: number) => void;
  selectedOrderId: null | number;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <H5>Your recent orders</H5>

      <div className="flex flex-col gap-2 divide-y divide-zinc-300 rounded-lg border border-zinc-300">
        {openOrderList.map((openOrder) => {
          const orderItemCount = openOrder.orderItemCount;
          return (
            <div
              key={openOrder.id}
              className="flex cursor-pointer items-center justify-between p-4"
              onClick={() => {
                onOpenOrderSelected(openOrder.id);
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="shrink-0">
                  <InputCheckbox
                    variant="circular"
                    required={false}
                    checked={selectedOrderId === openOrder.id}
                  />
                </div>
                <div className="flex flex-col">
                  <H5 className="text-zinc-800">
                    Edit open order #{openOrder.id}
                  </H5>
                  {!!openOrder.deliveryPlannedAt && (
                    <H6>
                      Planned delivery date:{" "}
                      <H6 className="inline font-bold">
                        {dayjs(openOrder.deliveryPlannedAt).format(
                          "ddd D MMM ",
                        )}
                      </H6>
                    </H6>
                  )}
                </div>
              </div>
              <div className="flex flex-col">
                <H5 className="text-end text-zinc-800">
                  {orderItemCount} {pluralize("item", orderItemCount)}
                </H5>
                <H6 className="text-end">{formatCurrency(openOrder.price)}</H6>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const EditExistingOrderDialog = ({
  active,
  openOrderList,
  onConfirm,
  onDismiss,
}: {
  active: boolean;
  openOrderList: OpenOrderListResponseType;
  onConfirm: (
    data:
      | { type: "ORDER_SELECTED"; order: OrderGetResponseType }
      | { type: "CREATE_NEW_ORDER" },
  ) => void;
  onDismiss: () => void;
}) => {
  const [createNewOrderSelected, setCreateNewOrderSelected] = useState(false);
  const [selectedOpenOrderId, setSelectedOpenOrderId] = useState<null | number>(
    null,
  );
  const selectedOpenOrderQuery = useOrder(selectedOpenOrderId ?? 0, {
    enabled: !!selectedOpenOrderId,
  });
  const noOptionSelected = !selectedOpenOrderId && !createNewOrderSelected;

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Existing open orders"}
        description={
          "Would you like to continue creating a new order or edit an existing order?"
        }
      />
      <DialogContent>
        <div className="flex flex-col gap-8">
          <RecentOrderList
            selectedOrderId={selectedOpenOrderId ?? null}
            openOrderList={openOrderList}
            onOpenOrderSelected={(selectedOrderId) => {
              setSelectedOpenOrderId(selectedOrderId);
              setCreateNewOrderSelected(false);
            }}
          />
          <CreateNewOrder
            checked={createNewOrderSelected}
            onCheckToggled={(isSelected) => {
              if (isSelected) {
                setCreateNewOrderSelected(isSelected);
                setSelectedOpenOrderId(null);
              }
            }}
          />
        </div>
      </DialogContent>

      <DialogCTA className={"sticky bottom-0 bg-white"}>
        <Button
          type={"button"}
          disabled={selectedOpenOrderQuery.isLoading || noOptionSelected}
          onClick={() => {
            if (createNewOrderSelected) {
              onConfirm({ type: "CREATE_NEW_ORDER" });
            }

            if (selectedOpenOrderId && selectedOpenOrderQuery.data) {
              onConfirm({
                type: "ORDER_SELECTED",
                order: selectedOpenOrderQuery.data,
              });
            }
          }}
        >
          CONFIRM
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
